import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Grid, Table, TableBody, TableCell, TableRow } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import { NumberUtils, Text } from '@pbt/pbt-ui-components'
import { findConstantIdByName } from '@pbt/pbt-ui-components/src/utils'
import { formatDate } from '@pbt/pbt-ui-components/src/utils/dateUtils'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import {
  canRestockInvoiceItem,
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
  isUnrefundable,
} from '~/components/dashboard/invoices/invoiceUtils'
import FinanceItemCreationDateCellBody from '~/components/dashboard/soapV2/charges/table/body/FinanceItemCreationDateCellBody'
import FeatureToggle from '~/constants/featureToggle'
import { FINANCE_CELL_WIDTH } from '~/constants/financeTable'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import { useGetChewyOrderState } from '~/store/hooks/retailOrderItems'
import {
  getChewyFreeShippingEnabled,
  getFeatureToggle,
  getRefundInvoiceState,
} from '~/store/reducers/constants'
import {
  getInvoiceV3,
  getInvoiceV3SubItemById,
} from '~/store/reducers/invoiceV3'
import type { InvoiceLineItem, RefundLineItem } from '~/types'

import FinanceItemDiscountCellBody from '../body/FinanceItemDiscountCellBody'
import FinanceItemProducerCellBody from '../body/FinanceItemProducerCellBody'
import FinanceItemQuantityCellBody from '../body/FinanceItemQuantityCellBody'
import FinanceItemSubTotalCellBody from '../body/FinanceItemSubTotalCellBody'
import FinanceItemTaxCellBodyBody from '../body/FinanceItemTaxCellBody'
import FinanceItemUnitPriceCellBody from '../body/FinanceItemUnitPriceCellBody'
import RetailOrderItemNameCellBody from '../body/RetailOrderItemNameCellBody'

const { DISCOUNT, PRODUCER, QUANTITY, REFUNDED, SUBTOTAL, TAX, UNIT_PRICE } =
  FINANCE_CELL_WIDTH

const useStyles = makeStyles(
  (theme) => ({
    creationDate: {
      opacity: '50%',
    },
    groupedCell: {
      padding: 0,
    },
    tableCellWithBorder: {
      borderRight: theme.constants.tableBorder,
    },
    refundedCell: {
      minWidth: REFUNDED,
      width: REFUNDED,
    },
    producerCell: {
      minWidth: PRODUCER,
      width: PRODUCER,
    },
    quantityCell: {
      minWidth: QUANTITY,
      width: QUANTITY,
    },
    unitPriceCell: {
      minWidth: UNIT_PRICE,
      width: UNIT_PRICE,
    },
    taxCell: {
      minWidth: TAX,
      width: TAX,
    },
    discountCell: {
      minWidth: DISCOUNT,
      width: DISCOUNT,
    },
    subtotalCell: {
      minWidth: SUBTOTAL,
      width: SUBTOTAL,
    },
    bundleCell: {
      justifyContent: 'center',
    },
    fullyRefunded: {
      color: theme.colors.lowAccentText,
    },
  }),
  { name: 'FinanceTableItemCells' },
)

export interface FinanceTableItemCellsProps {
  columnsLength: number
  currentItemId?: string
  handleSelectItem: (item: InvoiceLineItem | RetailOrderLineItem) => void
  invoiceHasRefunds?: boolean
  invoiceId?: string
  isExpandedSelector?: any
  isInvoice?: boolean
  isVoidedState?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  nested?: boolean
  parentItem?: InvoiceLineItem | RetailOrderLineItem
  selectedItem: InvoiceLineItem | RetailOrderLineItem | undefined
  showUsedPrepaid?: boolean
  soapId?: string
  tableCellClassName: string
  toggleExpand?: (item: InvoiceLineItem | RetailOrderLineItem) => void
}

const FinanceTableItemCells = ({
  columnsLength,
  handleSelectItem,
  isInvoice = false,
  invoiceHasRefunds = false,
  item,
  nested = false,
  selectedItem,
  soapId,
  tableCellClassName,
  isExpandedSelector,
  invoiceId,
  toggleExpand,
  showUsedPrepaid,
  isVoidedState = false,
  currentItemId,
  parentItem,
}: FinanceTableItemCellsProps) /* NOSONAR */ => {
  const classes = useStyles()

  const isIpoM0InvoiceRefundsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.INVOICE_BASED_REFUNDS),
  )
  const isAwoPmrRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_PMR_RIGHT_RAIL),
  )
  const isExpanded = useSelector(
    isExpandedSelector ? isExpandedSelector(item) : R.F,
  ) as boolean
  const itemSection = useSelector(getInvoiceV3SubItemById(currentItemId))

  const isGrouped = isGroupedInvoiceItem(item)

  const refundInvoiceStates = useSelector(getRefundInvoiceState)

  const invoice = useSelector(getInvoiceV3(invoiceId))

  const getChewyOrderState = useGetChewyOrderState()
  const isChewyFreeShippingEnabled = useSelector(getChewyFreeShippingEnabled)

  const { isMovedToChewyCart } = getChewyOrderState(
    invoice?.retailOrder?.state?.id,
  )

  const hasGroupedParentWithFixedPrice =
    useHasGroupedParentWithFixedPrice(parentItem)

  const hidePrices =
    hasGroupedParentWithFixedPrice ||
    (isChewyFreeShippingEnabled && isMovedToChewyCart)

  const voidedStateId = findConstantIdByName('Voided', refundInvoiceStates)

  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  const toggleExpandInner = (i: InvoiceLineItem | RetailOrderLineItem) => {
    if (isGrouped && toggleExpand) {
      toggleExpand(i)
    }
  }

  const isFinalized = Boolean(R.path(['soap', 'finalized'], itemSection))

  const isFullyRefunded =
    isIpoM0InvoiceRefundsEnabled && invoiceHasRefunds
      ? isGrouped && !isRetailOrderLineItem
        ? item.items?.every(isUnrefundable)
        : isUnrefundable(item) ||
          (item.subTotal === 0 && !canRestockInvoiceItem(item))
      : false

  const CommonProps = {
    isExpanded: isGrouped ? isExpanded : false,
    isGrouped,
    item,
    tableCellClassName,
    nested,
    isReadOnly: isInvoice ? isGrouped || isVoidedState || isFinalized : false,
    isFullyRefunded,
  }

  const notVoid = (refundItem: RefundLineItem) =>
    refundItem.refundedInvoice.state.id !== voidedStateId

  const refundLineItems = isRetailOrderLineItem
    ? []
    : R.filter(notVoid, item.refundLineItems || [])

  return (
    <>
      {isGrouped && !isRetailOrderLineItem && item.items ? (
        <TableCell
          className={classes.groupedCell}
          colSpan={columnsLength}
          id={item.group}
          scope="row"
        >
          <Grid container wrap="nowrap">
            {!isInvoice && !isAwoPmrRightRailEnabled && (
              <FinanceItemCreationDateCellBody {...CommonProps} />
            )}
            <RetailOrderItemNameCellBody
              showAsDisabled={isFullyRefunded}
              {...CommonProps}
              handleSelectItem={handleSelectItem}
              selectedItem={selectedItem}
              tableCellClassName={classNames(
                CommonProps.tableCellClassName,
                classes.tableCellWithBorder,
              )}
              toggleExpand={toggleExpandInner}
            />
            {isIpoM0InvoiceRefundsEnabled && invoiceHasRefunds && (
              <Grid
                className={classNames(
                  CommonProps.tableCellClassName,
                  classes.tableCellWithBorder,
                  classes.refundedCell,
                )}
              />
            )}
            <Grid
              className={classNames(
                classes.tableCellWithBorder,
                classes.producerCell,
              )}
            />
            <Grid
              className={classNames(
                classes.tableCellWithBorder,
                classes.quantityCell,
              )}
            />
            {isInvoice ? (
              <>
                <FinanceItemTaxCellBodyBody
                  {...CommonProps}
                  hidePrices
                  tableCellClassName={classes.bundleCell}
                  tableCellWithBorder={classes.tableCellWithBorder}
                />
                {R.prop('discount', item) ? (
                  <FinanceItemDiscountCellBody
                    {...CommonProps}
                    hidePrices
                    tableCellClassName={classes.bundleCell}
                    tableCellWithBorder={classes.tableCellWithBorder}
                  />
                ) : (
                  <Grid
                    className={classNames(
                      classes.tableCellWithBorder,
                      classes.discountCell,
                    )}
                  />
                )}
              </>
            ) : (
              <Grid
                className={classNames(
                  classes.tableCellWithBorder,
                  classes.unitPriceCell,
                )}
              />
            )}
            <Grid
              container
              item
              alignItems="center"
              className={classes.subtotalCell}
              justifyContent="flex-end"
              pl={1}
              pr={3}
            >
              {!item.declined && (
                <Text
                  className={classNames({
                    [classes.fullyRefunded]: isFullyRefunded,
                  })}
                  variant="body2"
                >
                  {NumberUtils.formatMoney(item.subTotal)}
                </Text>
              )}
            </Grid>
          </Grid>
          {isExpanded &&
            item.items.map((childItem) => (
              <Table key={`${childItem.bundleId}_${childItem.id}`}>
                <TableBody>
                  <TableRow id={childItem.logId}>
                    <FinanceTableItemCells
                      nested
                      columnsLength={columnsLength}
                      currentItemId={currentItemId}
                      handleSelectItem={handleSelectItem}
                      invoiceHasRefunds={invoiceHasRefunds}
                      invoiceId={invoiceId}
                      isInvoice={isInvoice}
                      item={childItem}
                      parentItem={item}
                      selectedItem={selectedItem}
                      showUsedPrepaid={showUsedPrepaid}
                      soapId={soapId}
                      tableCellClassName={tableCellClassName}
                    />
                  </TableRow>
                </TableBody>
              </Table>
            ))}
        </TableCell>
      ) : (
        <>
          {!isInvoice && !isAwoPmrRightRailEnabled && (
            <FinanceItemCreationDateCellBody {...CommonProps} />
          )}
          <RetailOrderItemNameCellBody
            isInvoice={isInvoice}
            showAsDisabled={isFullyRefunded}
            {...CommonProps}
            handleSelectItem={handleSelectItem}
            selectedItem={selectedItem}
            tableCellWithBorder={classes.tableCellWithBorder}
            toggleExpand={toggleExpandInner}
          />
          {isIpoM0InvoiceRefundsEnabled && invoiceHasRefunds && (
            <TableCell
              className={classNames(
                tableCellClassName,
                classes.tableCellWithBorder,
                classes.refundedCell,
              )}
            >
              <Grid container direction="column">
                {refundLineItems?.map((refundItem) => (
                  <Grid item key={refundItem.id}>
                    <Link
                      className={classes.fullyRefunded}
                      to={`/refund/${refundItem.refundedInvoice.id}`}
                    >
                      {formatDate(refundItem.refundedInvoice.creationDate)}
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </TableCell>
          )}

          <FinanceItemProducerCellBody
            {...CommonProps}
            allowModifyForInvoiceDoctorOnly={isInvoice}
            invoiceId={invoiceId}
            tableCellWithBorder={classes.tableCellWithBorder}
          />

          <FinanceItemQuantityCellBody
            {...CommonProps}
            showUsedPrepaid={showUsedPrepaid}
            tableCellWithBorder={classes.tableCellWithBorder}
          />

          {isInvoice ? (
            <>
              <FinanceItemTaxCellBodyBody
                {...CommonProps}
                hidePrices
                tableCellWithBorder={classes.tableCellWithBorder}
              />
              <FinanceItemDiscountCellBody
                {...CommonProps}
                hidePrices
                tableCellWithBorder={classNames(
                  tableCellClassName,
                  classes.tableCellWithBorder,
                )}
              />
            </>
          ) : (
            <FinanceItemUnitPriceCellBody
              {...CommonProps}
              tableCellWithBorder={classes.tableCellWithBorder}
            />
          )}

          <FinanceItemSubTotalCellBody
            {...CommonProps}
            hidePrices={hidePrices}
          />
        </>
      )}
    </>
  )
}

export default FinanceTableItemCells
